import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DocumentHeaderFooterService } from './document-header-footer.service';
import { DocumentHeader, DocumentFooter } from './document-header-footer.interface';
import { AddEditHeaderComponent } from './add-edit-header/add-edit-header.component';
import { AddEditFooterComponent } from './add-edit-footer/add-edit-footer.component';

@Component({
    selector: 'app-document-header-footer',
    templateUrl: './document-header-footer.component.html',
    styleUrls: ['./document-header-footer.component.scss']
})
export class DocumentHeaderFooterComponent implements OnInit {
    headers: DocumentHeader[] = [];
    footers: DocumentFooter[] = [];
    isLoading = false;
    selectedTabIndex = 0;

    constructor(
        private service: DocumentHeaderFooterService,
        private dialog: MatDialog,
        private snackBar: MatSnackBar
    ) {}

    ngOnInit(): void {
        this.loadHeaders();
        this.loadFooters();
    }

    loadHeaders(): void {
        this.isLoading = true;
        this.service.getHeadersList().subscribe({
            next: (response: any) => {
                this.headers = (response.data || []).map((item: any) => this.normalizeBoolean(item));
                this.isLoading = false;
            },
            error: (err) => {
                console.error('Error loading headers:', err);
                this.snackBar.open('Eroare la încărcarea anteturilor', 'Închide', { duration: 3000 });
                this.isLoading = false;
            }
        });
    }

    loadFooters(): void {
        this.isLoading = true;
        this.service.getFootersList().subscribe({
            next: (response: any) => {
                this.footers = (response.data || []).map((item: any) => this.normalizeBoolean(item));
                this.isLoading = false;
            },
            error: (err) => {
                console.error('Error loading footers:', err);
                this.snackBar.open('Eroare la încărcarea subsolurilor', 'Închide', { duration: 3000 });
                this.isLoading = false;
            }
        });
    }

    // Helper to convert string "0"/"1" to boolean
    private normalizeBoolean(item: any): any {
        return {
            ...item,
            is_default: item.is_default === true || item.is_default === 1 || item.is_default === '1',
            is_active: item.is_active === true || item.is_active === 1 || item.is_active === '1',
            show_pagination: item.show_pagination === true || item.show_pagination === 1 || item.show_pagination === '1'
        };
    }

    // ==================== HEADERS ====================

    openAddHeaderDialog(): void {
        const dialogRef = this.dialog.open(AddEditHeaderComponent, {
            width: '700px',
            data: { mode: 'add' }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadHeaders();
            }
        });
    }

    openEditHeaderDialog(header: DocumentHeader): void {
        const dialogRef = this.dialog.open(AddEditHeaderComponent, {
            width: '700px',
            data: { mode: 'edit', header }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadHeaders();
            }
        });
    }

    setHeaderDefault(header: DocumentHeader): void {
        if (header.is_default) return;

        this.service.setHeaderDefault(header.id).subscribe({
            next: () => {
                this.snackBar.open('Antet setat ca implicit', 'Închide', { duration: 3000 });
                this.loadHeaders();
            },
            error: (err) => {
                const message = err.error?.message || 'Eroare la setarea antetului implicit';
                this.snackBar.open(message, 'Închide', { duration: 3000 });
            }
        });
    }

    toggleHeaderActive(header: DocumentHeader): void {
        this.service.toggleHeaderActive(header.id).subscribe({
            next: () => {
                this.snackBar.open('Status actualizat', 'Închide', { duration: 3000 });
                this.loadHeaders();
            },
            error: (err) => {
                const message = err.error?.message || 'Eroare la actualizarea statusului';
                this.snackBar.open(message, 'Închide', { duration: 3000 });
            }
        });
    }

    deleteHeader(header: DocumentHeader): void {
        if (header.is_default) {
            this.snackBar.open('Nu puteți șterge antetul implicit', 'Închide', { duration: 3000 });
            return;
        }

        if (confirm('Sigur doriți să ștergeți acest antet?')) {
            this.service.deleteHeader(header.id).subscribe({
                next: () => {
                    this.snackBar.open('Antet șters cu succes', 'Închide', { duration: 3000 });
                    this.loadHeaders();
                },
                error: (err) => {
                    const message = err.error?.message || 'Eroare la ștergerea antetului';
                    this.snackBar.open(message, 'Închide', { duration: 3000 });
                }
            });
        }
    }

    // ==================== FOOTERS ====================

    openAddFooterDialog(): void {
        const dialogRef = this.dialog.open(AddEditFooterComponent, {
            width: '700px',
            data: { mode: 'add' }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadFooters();
            }
        });
    }

    openEditFooterDialog(footer: DocumentFooter): void {
        const dialogRef = this.dialog.open(AddEditFooterComponent, {
            width: '700px',
            data: { mode: 'edit', footer }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadFooters();
            }
        });
    }

    setFooterDefault(footer: DocumentFooter): void {
        if (footer.is_default) return;

        this.service.setFooterDefault(footer.id).subscribe({
            next: () => {
                this.snackBar.open('Subsol setat ca implicit', 'Închide', { duration: 3000 });
                this.loadFooters();
            },
            error: (err) => {
                const message = err.error?.message || 'Eroare la setarea subsolului implicit';
                this.snackBar.open(message, 'Închide', { duration: 3000 });
            }
        });
    }

    toggleFooterActive(footer: DocumentFooter): void {
        this.service.toggleFooterActive(footer.id).subscribe({
            next: () => {
                this.snackBar.open('Status actualizat', 'Închide', { duration: 3000 });
                this.loadFooters();
            },
            error: (err) => {
                const message = err.error?.message || 'Eroare la actualizarea statusului';
                this.snackBar.open(message, 'Închide', { duration: 3000 });
            }
        });
    }

    deleteFooter(footer: DocumentFooter): void {
        if (footer.is_default) {
            this.snackBar.open('Nu puteți șterge subsolul implicit', 'Închide', { duration: 3000 });
            return;
        }

        if (confirm('Sigur doriți să ștergeți acest subsol?')) {
            this.service.deleteFooter(footer.id).subscribe({
                next: () => {
                    this.snackBar.open('Subsol șters cu succes', 'Închide', { duration: 3000 });
                    this.loadFooters();
                },
                error: (err) => {
                    const message = err.error?.message || 'Eroare la ștergerea subsolului';
                    this.snackBar.open(message, 'Închide', { duration: 3000 });
                }
            });
        }
    }

    // Utility methods
    getContentTypeLabel(type: string): string {
        const labels: { [key: string]: string } = {
            'text': 'Text',
            'image': 'Imagine',
            'both': 'Text + Imagine',
            'pagination': 'Doar Paginare'
        };
        return labels[type] || type;
    }

    getAlignmentLabel(alignment: string): string {
        const labels: { [key: string]: string } = {
            'left': 'Stânga',
            'center': 'Centru',
            'right': 'Dreapta'
        };
        return labels[alignment] || alignment;
    }

    getDisplayModeLabel(displayMode: string): string {
        const labels: { [key: string]: string } = {
            'first_page': 'Doar prima pagină',
            'all_pages': 'Toate paginile'
        };
        return labels[displayMode] || displayMode || 'Doar prima pagină';
    }
}
